import React, { Fragment, useEffect } from "react";
import Routes from "./components/routing/Routes";
import { Provider } from "react-redux";
import store from "./store";
import { I18Provider } from "./i18n";
import { BrowserRouter as Router, Route } from "react-router-dom";
import {
  logoutUser,
  setCurrentUser,
  getMainPicture,
} from "./actions/userAction";
import setRequestHeader from "./components/common/setRequestHeader";
import jwt_decode from "jwt-decode";
import { toast } from "react-hot-toast";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { jsonParser } from "shared/utils/ppUtils";
import NoBalanceProvider from "reducers/no_balance";
import DynamicMeta from "shared/components/DynamicMeta";
import { getBrand } from "shared/brand";
import { getVersionString, VERSION_INFO } from "./shared/constants/version";
import { MaintenanceProvider } from "./context/MaintenanceContext";
import MaintenanceBanner from "./components/common/MaintenanceBanner";

const darkTheme = createTheme({
  palette: {
    // mode: "dark",
  },
});
if (localStorage.jwtUserToken) {
  setRequestHeader("token", localStorage.jwtUserToken);
  const decode = jwt_decode(localStorage.jwtUserToken);
  localStorage.setItem("token exp", decode.exp);
  store.dispatch(setCurrentUser(jsonParser(localStorage.getItem("companies"))));
  const currentTime = Date.now() / 1000;
  if (decode.exp < currentTime && !window.location.href.includes("/signin")) {
    store.dispatch(logoutUser());
    window.location.href = "/signin";
  }
}

if (
  !sessionStorage.getItem("rightImage") ||
  sessionStorage.getItem("rightImage") === "undefined" ||
  !sessionStorage.getItem("leftImage") ||
  sessionStorage.getItem("leftImage") === "undefined"
) {
  getMainPicture();
}

const App = () => {
  useEffect(() => {
    // Version and startup information
    console.log(`🚀 PhonePlay Frontend ${getVersionString()}`);
    console.log(`📝 Latest modifications: ${VERSION_INFO.latestModifications}`);
    console.log(`✨ Features: ${VERSION_INFO.features.join(', ')}`);

    // Setup continuous foreground notification listener
    const setupNotificationListener = async () => {
      try {
        const { messaging } = await import('./firebase');
        const { onMessage } = await import('firebase/messaging');

        // Set up continuous listener for foreground messages
        const unsubscribe = onMessage(messaging, (payload) => {
          console.log('📬 Foreground notification received:', payload);
          const title = payload?.data?.title || payload?.notification?.title || 'Notification';
          const body = payload?.data?.body || payload?.notification?.body || '';

          console.log(`Notification: ${title} - ${body}`);

          // Show notification using toast with language-aware positioning
          if (title && body) {
            // Ultra compact format - single line with bullet separator
            const compactBody = body.split('\n').filter(line => line.trim()).slice(0, 1).join(' • ');
            const shortTitle = title.length > 25 ? title.substring(0, 25) + '...' : title;

            toast(`${shortTitle} • ${compactBody}`, {
              icon: 'ℹ️',
              duration: 4000,
              style: {
                background: '#3b82f6',
                color: '#ffffff',
              },
            });
          }

          // Also show browser notification if permission is granted
          if (Notification.permission === 'granted') {
            try {
              const brand = getBrand();
              new Notification(title, {
                body: body,
                icon: brand.notificationIcon,
                badge: brand.notificationBadge,
                requireInteraction: false,
                silent: false
              });
            } catch (notifError) {
              console.error('Browser notification error:', notifError);
            }
          }
        });

        console.log('✅ Foreground notification listener initialized');

        // Return cleanup function
        return unsubscribe;
      } catch (error) {
        console.error('Firebase notifications setup failed:', error);
      }
    };

    setupNotificationListener();

    window.onkeydown = (e) => {
      if (e.ctrlKey && e.keyCode === 116) {
        sessionStorage.clear(); // clear all cache on hard refresh
      }
    };
  }, []);

  var lang;
  if (localStorage.langCity) {
    lang = localStorage.getItem("langCity");
  }

  let locale = lang || process.env.REACT_APP_DEFAULT_LANG;


  return (
    <Provider store={store}>
      <NoBalanceProvider>
        <I18Provider locale={locale}>
          <MaintenanceProvider>
            <Router>
              <ThemeProvider theme={darkTheme}>
                <Fragment>
                  <MaintenanceBanner />
                  <DynamicMeta />
                  <Route component={Routes} />
                </Fragment>
              </ThemeProvider>
            </Router>
          </MaintenanceProvider>
        </I18Provider>
      </NoBalanceProvider>
    </Provider>
  );
};

export default App;
